/**
 * The proof chip and its sheet (trust audit Phase 2; mockup
 * https://claude.ai/artifact/F74A1dCrf6HQVPiJBL9XHE, approved 10/28).
 *
 * One chip, three looks. Each starts with a symbol, so it reads the same
 * without color:
 *
 *   ✓ green   Proven      "From Elijah's passing test 10/17/25"
 *   ~ amber   Best guess  "Best guess: ServiceTitan date"
 *   ? dashed  Missing     "Missing: no report on file"
 *
 * On a busy screen (`place="busy"`) a proven fact is only a small gray ✓; it
 * turns green on hover and opens the sheet on tap. Best guess and Missing
 * always show their words. Tapping any chip opens the sheet: where the fact
 * came from, when, who, why, and the one button that fixes it.
 *
 * Staff screens only — never the customer's PDF, quote, invoice, email or
 * portal (`proof.test.ts`).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn, formatDateOnly, formatDateTime } from "@/lib/utils";
import {
    PROOF_SYMBOL,
    PROOF_WORD,
    isQuiet,
    proofAriaLabel,
    type Proof,
    type ProofFix,
    type ProofLabel,
    type ProofPlace,
} from "@/lib/proof";

const CHIP: Record<ProofLabel, { shell: string; dot: string }> = {
    FACT: {
        shell: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200",
        dot: "bg-emerald-700",
    },
    ESTIMATE: {
        shell: "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200",
        dot: "bg-amber-700",
    },
    MISSING: {
        shell: "border-dashed border-stone-400 bg-stone-100 text-red-900 dark:border-stone-500 dark:bg-stone-500/10 dark:text-red-200",
        dot: "bg-red-700",
    },
};

/** The sheet's colored word for each label. */
const SAY: Record<ProofLabel, string> = {
    FACT: "text-emerald-800 dark:text-emerald-300",
    ESTIMATE: "text-amber-800 dark:text-amber-300",
    MISSING: "text-red-800 dark:text-red-300",
};

export function ProofSymbol({ label, className }: { label: ProofLabel; className?: string }) {
    return (
        <span
            aria-hidden
            className={cn(
                "grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full text-[11.5px] font-extrabold leading-none text-white",
                CHIP[label].dot,
                className,
            )}
        >
            {PROOF_SYMBOL[label]}
        </span>
    );
}

/**
 * The chip itself. `onFix` handles a fix that is a verb on this screen
 * ("edit_due_date", "ask_tech"); a fix with an `href` just goes there.
 */
export function ProofChip({
    proof,
    place = "page",
    onFix,
    className,
    testId,
}: {
    proof: Proof;
    place?: ProofPlace;
    onFix?: (fix: ProofFix) => void;
    className?: string;
    testId?: string;
}) {
    const [open, setOpen] = useState(false);
    const quiet = isQuiet(proof, place);
    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(true);
                }}
                aria-label={proofAriaLabel(proof)}
                title={quiet ? proof.words : undefined}
                data-testid={testId ?? "proof-chip"}
                data-proof={proof.label}
                data-quiet={quiet ? "true" : undefined}
                className={cn(
                    "group inline-flex max-w-full cursor-pointer items-center text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-600 focus-visible:ring-offset-2",
                    quiet
                        ? "relative rounded-full before:absolute before:-inset-2 before:content-['']"
                        : cn(
                              "gap-1.5 rounded-full border py-[3px] pl-[3px] pr-2.5 text-[12.5px] font-medium leading-tight",
                              CHIP[proof.label].shell,
                              // In a table cell the words stay on one line; the table scrolls.
                              place === "busy" && "shrink-0 whitespace-nowrap",
                          ),
                    className,
                )}
            >
                {quiet ? (
                    <span
                        aria-hidden
                        className="grid h-4 w-4 place-items-center rounded-full bg-gray-400 text-[10px] font-extrabold leading-none text-white transition-colors group-hover:bg-emerald-700 group-focus-visible:bg-emerald-700"
                    >
                        {PROOF_SYMBOL.FACT}
                    </span>
                ) : (
                    <>
                        <ProofSymbol label={proof.label} />
                        <span className="min-w-0">{proof.words}</span>
                    </>
                )}
            </button>
            <ProofSheet proof={proof} open={open} onClose={() => setOpen(false)} onFix={onFix} />
        </>
    );
}

function whenText(v: string): string {
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? formatDateOnly(v) : formatDateTime(v);
}

/** The proof sheet: where it came from, when, who, why, and the fix. */
export function ProofSheet({
    proof,
    open,
    onClose,
    onFix,
}: {
    proof: Proof;
    open: boolean;
    onClose: () => void;
    onFix?: (fix: ProofFix) => void;
}) {
    return (
        <Dialog open={open} onClose={onClose} title="How sure is this?" className="max-w-md">
            <ProofSheetBody proof={proof} onClose={onClose} onFix={onFix} />
        </Dialog>
    );
}

/** The sheet's contents (also rendered on its own for the ⓘ pictures). */
export function ProofSheetBody({
    proof,
    onClose,
    onFix,
}: {
    proof: Proof;
    onClose: () => void;
    onFix?: (fix: ProofFix) => void;
}) {
    const navigate = useNavigate();
    const fix = proof.fix && (proof.fix.href || (proof.fix.action && onFix)) ? proof.fix : null;
    const go = (href: string) => {
        onClose();
        navigate(href);
    };
    const rows: Array<[string, string | null]> = [
        ["Source", proof.source ?? "Nothing on file"],
        ["Date", proof.date ? whenText(proof.date) : null],
        ["Who", proof.who],
    ];
    return (
        <div className="space-y-4" data-testid="proof-sheet" data-proof={proof.label}>
            <div className="space-y-2">
                <p
                    className={cn(
                        "flex items-center gap-2 text-[13px] font-extrabold uppercase tracking-wide",
                        SAY[proof.label],
                    )}
                >
                    <ProofSymbol label={proof.label} className="h-6 w-6 text-[13px]" />
                    {PROOF_WORD[proof.label]}
                </p>
                <p className="text-lg font-bold leading-snug tracking-tight">{proof.title}</p>
            </div>
            <dl className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
                {rows
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                        <div key={k} className="contents">
                            <dt className="pt-0.5 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {k}
                            </dt>
                            <dd className="m-0">{v}</dd>
                        </div>
                    ))}
            </dl>
            {proof.why && (
                <p className="rounded-lg bg-muted/60 px-3 py-2 text-[13.5px] text-foreground/85">{proof.why}</p>
            )}
            <div className="flex flex-wrap justify-end gap-2 pt-1">
                {fix && (
                    <Button
                        data-testid="proof-fix"
                        onClick={() => {
                            if (fix.href) go(fix.href);
                            else {
                                onClose();
                                onFix?.(fix);
                            }
                        }}
                    >
                        {fix.label}
                    </Button>
                )}
                {proof.open && (
                    <Button variant="outline" data-testid="proof-open" onClick={() => go(proof.open!.href)}>
                        {proof.open.label}
                    </Button>
                )}
                <Button variant={fix || proof.open ? "ghost" : "default"} onClick={onClose}>
                    Done
                </Button>
            </div>
        </div>
    );
}
